import React, { useState } from 'react';
import { Info, ExternalLink, CreditCard as Edit3, RotateCcw } from 'lucide-react';
import { formatCurrency } from '../utils/formatting';
import { calculateProgressiveTax } from '../utils/taxCalculations';
import { SalaryCalculationResult, OverseasTerritoryId, CalculatedCotisation } from '../types';
import { findOverseasTerritoryById } from '../types';
import EditableAmount from './ui/EditableAmount';

interface DetailedCalculationBreakdownProps {
  salaryData: SalaryCalculationResult;
  isAnnual: boolean;
  calculateTaxAmount: number;
  netAfterTaxWithCustomRate: { annual: number; monthly: number };
  selectedOverseasTerritory?: OverseasTerritoryId;
  customTaxRate: number | null;
  onCustomTaxRateChange: (rate: number | null) => void;
}

export default function DetailedCalculationBreakdown({
  salaryData,
  isAnnual,
  calculateTaxAmount,
  netAfterTaxWithCustomRate,
  selectedOverseasTerritory,
  customTaxRate,
  onCustomTaxRateChange
}: DetailedCalculationBreakdownProps) {
  const [showTaxTooltip, setShowTaxTooltip] = useState<boolean>(false);
  const [showTaxDetailTooltip, setShowTaxDetailTooltip] = useState<boolean>(false);
  const [isEditingCustomTaxRate, setIsEditingCustomTaxRate] = useState<boolean>(false);
  const [showCotisationTooltip, setShowCotisationTooltip] = useState<string | null>(null);

  const handleEditCustomTaxRate = () => {
    console.log('🔍 Before setting isEditingCustomTaxRate:', isEditingCustomTaxRate);
    setIsEditingCustomTaxRate(true);
    console.log('🔄 After setting isEditingCustomTaxRate to true');
  };

  const handleSaveCustomTaxRate = (rate: number): boolean => {
    if (rate >= 0 && rate <= 100) {
      onCustomTaxRateChange(rate);
      setIsEditingCustomTaxRate(false);
      return true;
    }
    return false;
  };

  const handleCancelCustomTaxRate = () => {
    setIsEditingCustomTaxRate(false);
  };

  const handleResetTaxRate = () => {
    onCustomTaxRateChange(null);
    setIsEditingCustomTaxRate(false);
  };

  const getEffectiveTaxRate = () => {
    if (customTaxRate !== null) {
      return customTaxRate;
    }
    if (salaryData.taxableIncome > 0) {
      return (calculateTaxAmount / salaryData.taxableIncome) * 100;
    }
    return 0;
  };

  return (
    <div className="p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-b-lg border-t border-gray-200">
      <h4 className="font-medium text-gray-800 mb-3 text-sm sm:text-base">Détail du calcul</h4>
      
      <div className="space-y-2 text-xs sm:text-sm">
        <div className="flex justify-between items-start gap-2">
          <span className="font-semibold text-gray-800">Salaire brut {isAnnual ? 'annuel' : 'mensuel'} (Traitement Indiciaire):</span>
          <span className="font-bold text-blue-900 text-right break-all">
            {formatCurrency(isAnnual ? salaryData.grossAnnual : salaryData.grossMonthly)}
          </span>
        </div>

        {/* Revenus avec primes et indemnités */}
        <div className="flex justify-between items-start gap-2">
          <span className="text-gray-600">+ Primes et indemnités {isAnnual ? '' : '*'} :</span>
          <span className="font-medium text-green-600 text-right break-all">
            + {formatCurrency(isAnnual ? salaryData.primesAmount : salaryData.primesAmount / 12)}
          </span>
        </div>

        <div className="flex justify-between items-start gap-2 border-t pt-2">
          <span className="font-semibold text-gray-800">= Revenus avec primes :</span>
          <span className="font-bold text-orange-900 text-right break-all">
            {formatCurrency(isAnnual ? salaryData.withPrimesAnnual : salaryData.withPrimesMonthly)}
          </span>
        </div>

        {/* Détail des cotisations sociales */}
        {salaryData.calculatedCotisations && salaryData.calculatedCotisations.length > 0 && (
          <div className="space-y-1 border-t pt-2">
            <div className="text-gray-600 font-medium mb-1">- Cotisations sociales :</div>
            {salaryData.calculatedCotisations.map((cotisation) => (
              <div key={cotisation.nom} className="flex justify-between items-start gap-2 ml-4">
                <div className="flex items-center gap-1">
                  <span className="text-gray-600 break-words">{cotisation.nom} :</span>
                  <div className="relative">
                    <button
                      onMouseEnter={() => setShowCotisationTooltip(cotisation.nom)}
                      onMouseLeave={() => setShowCotisationTooltip(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                    
                    {/* Cotisation Tooltip */}
                    {showCotisationTooltip === cotisation.nom && (
                      <div className="absolute z-20 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                        <h4 className="font-semibold text-yellow-300 mb-2">{cotisation.nom}</h4>
                        <div className="space-y-2">
                          <p className="text-gray-200">{cotisation.detail}</p>
                          <div className="border-t border-gray-600 pt-2">
                            <div className="mb-2">
                              <div className="text-yellow-300 font-medium mb-1">Base de calcul :</div>
                              <div className="text-gray-200 text-xs leading-relaxed whitespace-pre-line">
                                {cotisation.calculDetail.split('\n')[0]}
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span>Montant de l'assiette :</span>
                                <span className="text-blue-300">{formatCurrency(cotisation.assietteMontant)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Taux appliqué :</span>
                                <span className="text-yellow-300">{cotisation.tauxApplique.toFixed(2)}%</span>
                              </div>
                              <div className="flex justify-between font-medium">
                                <span>Cotisation mensuelle :</span>
                                <span className="text-red-300">{formatCurrency(cotisation.montantMensuel)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-300 whitespace-pre-line">
                              {cotisation.calculDetail.split('\n').slice(1).join('\n')}
                            </div>
                          </div>
                        </div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                      </div>
                    )}
                  </div>
                </div>
                <span className="font-medium text-red-600 text-right break-all">
                  - {formatCurrency(isAnnual ? cotisation.montantAnnuel : cotisation.montantMensuel)}
                </span>
              </div>
            ))}
            
            {/* Total des cotisations sociales */}
            <div className="flex justify-between items-start gap-2 ml-4 pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-700">Total cotisations sociales :</span>
              <span className="font-bold text-red-700 text-right break-all">
                - {formatCurrency(
                  isAnnual 
                    ? salaryData.calculatedCotisations.reduce((total, cotisation) => total + cotisation.montantAnnuel, 0)
                    : salaryData.calculatedCotisations.reduce((total, cotisation) => total + cotisation.montantMensuel, 0)
                )}
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-between items-start gap-2 border-t pt-2">
          <span className="font-semibold text-gray-800">= Revenus nets avant impôt :</span>
          <span className="font-bold text-teal-600 text-right break-all">
            {formatCurrency(isAnnual ? salaryData.netAfterChargesAnnual : salaryData.netAfterChargesMonthly)}
          </span>
        </div>
        
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-1">
            <span className="text-gray-600 break-words">- Abattement 10% (frais pro){isAnnual ? '' : ' *'} :</span>
            <div className="relative">
              <button
                onMouseEnter={() => setShowTaxTooltip(true)}
                onMouseLeave={() => setShowTaxTooltip(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Info className="w-3 h-3" />
              </button>
              
              {/* Tax Allowance Tooltip */}
              {showTaxTooltip && (
                <div className="absolute z-20 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                  <h4 className="font-semibold text-yellow-300 mb-2">Abattement pour frais professionnels</h4>
                  <div className="space-y-1">
                    <p>L'administration fiscale applique automatiquement un abattement de 10% sur les revenus salariaux pour tenir compte des frais professionnels.</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between">
                        <span>Taux :</span>
                        <span className="text-yellow-300">10%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Minimum :</span>
                        <span className="text-green-300">429 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Maximum :</span>
                        <span className="text-red-300">12 652 €</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-600">
                      <div className="flex justify-between">
                        <span>Votre abattement :</span>
                        <span className="text-yellow-300">{formatCurrency(salaryData.abattementAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Revenu imposable :</span>
                        <span className="text-blue-300">{formatCurrency(salaryData.taxableIncome)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                </div>
              )}
            </div>
          </div>
          <span className="font-medium text-green-600 text-right break-all">
            - {formatCurrency(isAnnual ? salaryData.abattementAmount : salaryData.abattementAmount / 12)}
          </span>
        </div>

        {/* Abattement fiscal DOM-TOM */}
        {selectedOverseasTerritory && salaryData.domTomTaxAbatement > 0 && (
          (() => {
            const territory = findOverseasTerritoryById(selectedOverseasTerritory);
            if (!territory?.abatementRate) return null;
            
            return (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-600 break-words">
              - Abattement fiscal {territory.abatementRate}% - {territory.name}{isAnnual ? '' : ' *'} :
            </span>
            <span className="font-medium text-green-600 text-right break-all">
              - {formatCurrency(isAnnual ? salaryData.domTomTaxAbatement : salaryData.domTomTaxAbatement / 12)}
            </span>
          </div>
            );
          })()
        )}
        
        <div className="flex justify-between items-start gap-2 border-t pt-2">
          <span className="text-gray-600">= Revenu imposable{isAnnual ? '' : ' *'} :</span>
          <span className="font-medium text-blue-800 text-right break-all">
            {formatCurrency(isAnnual ? salaryData.taxableIncome : salaryData.taxableIncome / 12)}
          </span>
        </div>
        
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-1">
            <span className="text-gray-600 break-words">- Impôts{isAnnual ? '' : ' *'} :</span>
            <div className="relative">
              <button
                aria-label="Informations sur le calcul des impôts"
                onMouseEnter={() => setShowTaxDetailTooltip(true)}
                onMouseLeave={() => setShowTaxDetailTooltip(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Info className="w-3 h-3" />
              </button>
              
              {/* Tax Detail Tooltip */}
              {showTaxDetailTooltip && (
                <div className="absolute z-20 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                  {customTaxRate !== null ? (
                    <div className="mb-3">
                      <h4 className="font-semibold text-yellow-300 mb-2">Taux d'impôt personnalisé</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Taux appliqué :</span>
                          <span className="text-yellow-300">{customTaxRate.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Revenu imposable :</span>
                          <span className="text-blue-300">{formatCurrency(salaryData.taxableIncome)}</span>
                        </div>
                      </div>
                      <div className="border-t border-gray-600 mt-2 pt-2">
                        <div className="flex justify-between font-medium">
                          <span>Impôt calculé :</span>
                          <span className="text-red-300">{formatCurrency(calculateTaxAmount)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <>
                  <div className="mb-3">
                    <h4 className="font-semibold text-yellow-300 mb-2">Barème de l'impôt sur le revenu 2024</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Jusqu'à 11 497 €</span>
                        <span className="text-green-300">0%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>De 11 498 € à 29 315 €</span>
                        <span className="text-yellow-300">11%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>De 29 316 € à 83 823 €</span>
                        <span className="text-orange-300">30%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>De 83 824 € à 180 294 €</span>
                        <span className="text-red-300">41%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Plus de 180 294 €</span>
                        <span className="text-red-400">45%</span>
                      </div>
                    </div>
                  </div>
                  
                  {salaryData && (
                    <div className="border-t border-gray-600 pt-2">
                      <div className="text-yellow-300 font-medium mb-1">
                        Calcul détaillé pour {formatCurrency(salaryData.taxableIncome)} :
                      </div>
                      <div className="space-y-1 text-xs">
                        {(() => {
                          const taxCalculation = calculateProgressiveTax(salaryData.taxableIncome);
                          return taxCalculation.breakdown.map((bracket, index) => (
                            <div key={index} className="flex justify-between">
                              <span className="text-gray-300">{bracket.bracket} :</span>
                              <span className="text-blue-300">{formatCurrency(bracket.amount)}</span>
                            </div>
                          ));
                        })()}
                      </div>
                      <div className="border-t border-gray-600 mt-2 pt-1 flex justify-between font-medium">
                        <span>Total impôt :</span>
                        <span className="text-red-300">{formatCurrency(calculateTaxAmount)}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span>Taux effectif :</span>
                        <span className="text-yellow-300">
                          {(() => {
                            // Vérification explicite pour NaN
                            if (isNaN(salaryData.taxableIncome) || isNaN(calculateTaxAmount)) {
                              return '0.0%';
                            }
                            
                            if (salaryData.taxableIncome <= 0 || calculateTaxAmount <= 0) {
                              return '0.0%';
                            }
                            return (calculateTaxAmount / salaryData.taxableIncome * 100).toFixed(1) + '%';
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                  </>
                  )}
                  
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="font-medium text-red-600 text-right whitespace-nowrap text-lg">
              - {formatCurrency(isAnnual ? calculateTaxAmount : calculateTaxAmount / 12)}
            </span>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <span className="text-gray-600 text-sm whitespace-nowrap">
                Taux actuel : {getEffectiveTaxRate().toFixed(1)}%
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {console.log('🎯 Rendering tax rate controls, isEditingCustomTaxRate:', isEditingCustomTaxRate)}
                {isEditingCustomTaxRate ? (
                  <div className="flex items-center gap-2">
                    {console.log('✏️ Rendering EditableAmount for custom tax rate')}
                    <input
                      type="number"
                      value={customTaxRate || getEffectiveTaxRate()}
                      onChange={(e) => {
                        const newRate = Number(e.target.value);
                        console.log('📝 Tax rate input changed:', newRate);
                        if (newRate >= 0 && newRate <= 100) {
                          onCustomTaxRateChange(newRate);
                        }
                      }}
                      onBlur={() => {
                        console.log('👋 Tax rate input lost focus, closing edit mode');
                        setIsEditingCustomTaxRate(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          console.log('⏎ Enter pressed, closing edit mode');
                          setIsEditingCustomTaxRate(false);
                        }
                        if (e.key === 'Escape') {
                          console.log('⎋ Escape pressed, closing edit mode');
                          setIsEditingCustomTaxRate(false);
                        }
                      }}
                      className="w-16 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      max="100"
                      step="0.1"
                      autoFocus
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                ) : (
                <button
                  onClick={handleEditCustomTaxRate}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
                  title="Personnaliser le taux d'impôt"
                >
                  Modifier
                </button>
                )}
                {customTaxRate !== null && (
                  <button
                    onClick={handleResetTaxRate}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium whitespace-nowrap"
                    title="Revenir au calcul automatique"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-start gap-2 border-t pt-2">
          <span className="font-semibold text-gray-800">= Revenus nets après impôts :</span>
          <span className="font-bold text-green-900 text-right break-all">
            {formatCurrency(isAnnual ? netAfterTaxWithCustomRate.annual : netAfterTaxWithCustomRate.monthly)}
          </span>
        </div>
        
        {!isAnnual && (
          <div className="mt-3 p-2 sm:p-3 bg-yellow-50 rounded border border-yellow-200">
            <p className="text-xs sm:text-sm text-yellow-800">
              <strong>* Note :</strong> L'abattement, le revenu imposable et les impôts sont calculés sur une base annuelle puis divisés par 12 pour l'affichage mensuel.
              {customTaxRate !== null && (
                <span className="block mt-1">
                  <strong>Taux personnalisé :</strong> Un taux d'impôt de {customTaxRate.toFixed(1)}% est appliqué au lieu du barème progressif.
                </span>
              )}
            </p>
            
            <div className="mt-2">
              <a
                href="https://cfspart-idp.impots.gouv.fr/oauth2/authorize"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 underline"
              >
                <ExternalLink className="w-3 h-3" />
                Consulter mon espace personnel impots.gouv.fr
              </a>
            </div>
          </div>
        )}
        
        {/* Lien impots.gouv.fr pour vue annuelle */}
        {isAnnual && (
          <div className="mt-3 p-2 sm:p-3 bg-yellow-50 rounded border border-yellow-200">
            {customTaxRate !== null && (
              <p className="text-xs sm:text-sm text-yellow-800 mb-2">
                <strong>Taux personnalisé :</strong> Un taux d'impôt de {customTaxRate.toFixed(1)}% est appliqué au lieu du barème progressif.
              </p>
            )}
            <div className="mt-2">
              <a
                href="https://cfspart-idp.impots.gouv.fr/oauth2/authorize"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 underline"
              >
                <ExternalLink className="w-3 h-3" />
                Consulter mon espace personnel impots.gouv.fr
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}